import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, Space, Button, Empty, message } from "antd";
import moment from "moment";
import { ExclamationCircleTwoTone } from "@ant-design/icons";
import API from "../../../utils/API/API";
import {
    PushpinTwoTone,
    BellTwoTone,
    CloseCircleTwoTone,
    InfoCircleTwoTone,
    SettingTwoTone,
    MessageTwoTone,
    GiftTwoTone,
} from "@ant-design/icons";


// helper to return icon by type
const getNotificationIcon = (type) => {
    switch (type) {
        case "New Appointment":
            return <PushpinTwoTone twoToneColor="#87d068" style={{ fontSize: 25 }} />;
        case "Reschedule":
            return <BellTwoTone twoToneColor="#faad14" style={{ fontSize: 25 }} />;
        case "Cancel":
            return <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 25 }} />;
        case "New Walked-in":
            return <InfoCircleTwoTone twoToneColor="#b37feb" style={{ fontSize: 25 }} />;
        case "System":
            return <SettingTwoTone twoToneColor="#fa541c" style={{ fontSize: 25 }} />;
        case "Review":
            return <MessageTwoTone style={{ fontSize: 25 }} />;
        case "Gift Card":
            return <GiftTwoTone twoToneColor="#fcd461" style={{ fontSize: 25 }} />;
        default:
            return null;
    }
};

/**
 * ActivityNotificationsList (non-System)
 *
 * Props:
 * - pageSize?: number                       // default 10
 * - counts?: {
 *     totalNotifications: number,
 *     totalSystemNotifications?: number,    // preferred for accurate total
 *     unreadSystemNotifications?: number    // fallback ONLY if totalSystemNotifications isn't available
 *   }
 * - onItemClick?: (url, item) => void
 * - onMarkAsRead?: (item) => Promise<void> | void
 * - className?: string
 * - fetchInit?: RequestInit                 // e.g., { credentials: "include" }
 *
 * Notes:
 * - Backend returns mixed notifications; we filter out { type: "System" } on the client.
 * - For correct "Load more" behavior, pass totalActivityCount OR counts with totalSystemNotifications.
 */
const ActivityNotificationsList = ({
    pageSize = 10,
    onItemClick,
    onMarkAsRead,
    className,
    fetchInit,
}) => {
    const [items, setItems] = useState([]);       // non-System accumulated
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const abortRef = useRef(null);


    const fetchPage = useCallback(
        async (pageToFetch) => {
            setLoading(true);
            abortRef.current?.abort?.();
            const controller = new AbortController();
            abortRef.current = controller;

            try {
                const params = new URLSearchParams({
                    page: String(pageToFetch),
                    limit: String(pageSize),
                });

                const res = await API.get(
                    `/api/notifications?${params.toString()}`,
                    {
                        ...fetchInit,
                        signal: controller.signal,
                    }
                );

                if (res.status !== 200) {
                    const text = await res.text().catch(() => "");

                    throw new Error(text || `Request failed with ${res.status}`);
                }

                const { data } = res;

                // Avoid duplicates if user spam-clicks load more
                setItems((prev) => {
                    const seen = new Set(prev.map((x) => String(x._id)));
                    const merged = [...prev];
                    for (const d of data) {
                        const id = String(d._id);
                        if (!seen.has(id)) {
                            merged.push(d);
                            seen.add(id);
                        }
                    }
                    return merged;
                });

                setHasMore(Array.isArray(data) && data.length === pageSize);
            } catch (err) {
                if (err.name !== "AbortError") {
                    message.error("Failed to load system notifications.");
                    setHasMore(false);
                }
            } finally {
                setLoading(false);
            }
        },
        [pageSize, fetchInit]
    );

    useEffect(() => {
        fetchPage(page);
        return () => abortRef.current?.abort?.();
    }, [page, fetchPage]);

    const loadMoreNode = useMemo(() => {
        if (!hasMore) return null;
        return (
            <div style={{ textAlign: "center", marginTop: 12, height: 32, lineHeight: "32px" }}>
                <Button onClick={() => setPage((p) => p + 1)} loading={loading}>
                    Load more
                </Button>
            </div>
        );
    }, [hasMore, loading]);

    if (!loading && items.length === 0) {
        return (
            <Empty
                className={className}
                description="No activity notifications"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
        );
    }

    return (
        <List
            className={className}
            loading={loading && items.length === 0}
            loadMore={loadMoreNode}
            dataSource={items}
            renderItem={(item) => (
                <List.Item
                    className="notification-item-container"
                    key={item._id}
                    style={item.read ? { padding: 10, backgroundColor: "#ededed" } : { padding: 10 }}
                    onClick={() => {
                        const url = item?.links?.[0]?.url;
                        if (url && onItemClick) onItemClick(url, item);
                    }}
                >
                    <List.Item.Meta
                        title={
                            <Space style={item.read ? { fontWeight: "normal" } : { fontWeight: "bold" }}>
                                {getNotificationIcon(item.type)}  {item.title}
                            </Space>
                        }
                        description={
                            <div>
                                <p style={{ margin: 0 }}>
                                    {item.message} <br />
                                    <span style={item.read ? { color: "inherit" } : { color: "#1890ff" }}>
                                        {moment(item.createdAt).fromNow()} at {moment(item.createdAt).format("hh:mm a")}
                                    </span>
                                    <span className="notification-item-read-button">
                                        {!item.read && (
                                            <ExclamationCircleTwoTone
                                                twoToneColor="#1890ff"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                        await onMarkAsRead?.(item);
                                                        // optimistic UI update
                                                        setItems((prev) =>
                                                            prev.map((n) =>
                                                                String(n._id) === String(item._id) ? { ...n, read: true } : n
                                                            )
                                                        );
                                                    } catch {
                                                        message.error("Failed to mark as read.");
                                                    }
                                                }}
                                                className="notification-item-read-button"
                                            />
                                        )}
                                    </span>
                                </p>
                            </div>
                        }
                    />
                </List.Item>
            )}
        />
    );
};

export default ActivityNotificationsList;